import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import {
  type Region,
  type VatRegistrationDto,
  VatStatus,
  getPartiesVatRegistration,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { NO_VAT_NUMBER, VAT_NUMBER_PATTERN } from "Schemas/vatNumberSchema";

const VAT_REGISTRATION_QUERY_KEY = "VAT_REGISTRATION";

export const vatRegistrationKeys = {
  all: [VAT_REGISTRATION_QUERY_KEY],
  byNumbers: (
    region: Region | null | undefined,
    taxNumber: string,
    vatNumber: string,
  ) => [...vatRegistrationKeys.all, region, taxNumber, vatNumber],
};

// An answer stays good for a few minutes, so the same number is not asked twice while one form is open.
// "Unknown" is no answer (outage, nothing to ask about) — the next ask must reach the register again.
const getVatRegistrationStaleTime = (query: {
  state: { data?: VatRegistrationDto };
}) => (query.state.data?.vatStatus === VatStatus.Unknown ? 0 : 1000 * 60 * 5);

// Worth asking only with something to ask about, and never about a half-typed IČ DPH — the backend
// would answer 400 on every keystroke.
export const canAskVatRegistry = (
  region: Region,
  taxNumber: string,
  vatNumber: string,
): boolean =>
  (taxNumber.length > 0 || vatNumber.length > 0) &&
  (vatNumber === NO_VAT_NUMBER || VAT_NUMBER_PATTERN[region].test(vatNumber));

// Shared by both query shapes below; a zero-argument function so it fits any QueryFunction slot.
const fetchVatRegistration =
  (region: Region, taxNumber: string, vatNumber: string) => () =>
    processResponseWithApiProblemDetails(() =>
      getPartiesVatRegistration(region, taxNumber, vatNumber),
    )();

// What the market's VAT register (CZ → CRPDPH, SK → VIES) says about a DIČ / IČ DPH pair. A preview only —
// the party save verifies again. For imperative use (queryClient.fetchQuery) in form event handlers.
export const vatRegistrationQueryOptions = (
  region: Region,
  taxNumber: string,
  vatNumber: string,
) =>
  queryOptions({
    queryKey: vatRegistrationKeys.byNumbers(region, taxNumber, vatNumber),
    queryFn: fetchVatRegistration(region, taxNumber, vatNumber),
    refetchOnWindowFocus: false,
    staleTime: getVatRegistrationStaleTime,
  });

// `region: null` skips the lookup — nothing is asked without a market.
export const useVatRegistrationQuery = (
  region: Region | null | undefined,
  taxNumber: string,
  vatNumber: string,
) =>
  useQuery({
    queryKey: vatRegistrationKeys.byNumbers(region, taxNumber, vatNumber),
    queryFn:
      region != null && canAskVatRegistry(region, taxNumber, vatNumber)
        ? fetchVatRegistration(region, taxNumber, vatNumber)
        : skipToken,
    refetchOnWindowFocus: false,
    staleTime: getVatRegistrationStaleTime,
  });
